import { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import TableIdButton from "@lib/components/MiniComponents/Buttons/TableIdButton";
import { shortenString } from "@lib/helpers/StringHelper";
import { ColumnConfig, generateColumns } from "@lib/helpers/TableHelper";
import { TableColumnsProps } from "@lib/types/General";
import {
  XAICall,
  XAICallStatus,
  XAIClientMood,
  XAIDisconnectionResult,
} from "@lib/types/xAI";
import CustomerName from "@lib/components/MiniComponents/Others/CustomerName";
import { StyledFlex } from "@lib/components/MiniComponents/Table/index.styled";
import { ActionButton } from "@lib/components/MiniComponents/Buttons";
import { Tooltip } from "antd";
import VoicePlayer from "@lib/components/MiniComponents/Voice";
import {
  CoinPriceTextFormat,
  XAICallStatusTag,
  XAIClientMoodTag,
  XAIDisconnectionResultTag,
  Keywords,
} from "@lib/components/MiniComponents/Others";
import CallLength from "@lib/components/MiniComponents/Others/CallLength";

type Props<T> = TableColumnsProps<T> & {
  handleSelect: (item: XAICall) => void;
  biggestDuration: number;
  biggestCost: number;
};

const getColumns = <T extends XAICall>(props: Props<T>): ColumnsType<T> => {
  const {
    handleSelect,
    biggestDuration,
    biggestCost,
    messages,
    detailContext,
  } = props;

  const columnsConfig: ColumnConfig<T>[] = [
    {
      title: "common.callId",
      dataIndex: "rawCallback.call_id",
      width: "130px",
      filterType: "search",
      render: (_, r) => (
        <TableIdButton text={`${shortenString(r?.callId || "")}`} />
      ),
    },

    {
      title: "common.customer",
      dataIndex: "customer.id",
      key: "customer.name",
      width: "150px",
      render: (_, r) => <CustomerName customer={r?.customer} link />,
      hidden: !!detailContext,
    },

    {
      title: "common.firstName",
      dataIndex: "lead.firstName",
      width: "150px",
      filterType: "search",
    },
    {
      title: "common.lastName",
      dataIndex: "lead.lastName",
      width: "150px",
      filterType: "search",
    },

    {
      title: "common.updatedAt",
      dataIndex: "updatedAt",
      width: "150px",
      render: (d) => dayjs(d).format("DD/MM/YYYY HH:mm"),
    },

    {
      title: "common.callStatus",
      dataIndex: "callStatus",
      width: "150px",
      filterType: "dropdown",
      filters: Object.values(XAICallStatus).map((status) => ({
        text: <XAICallStatusTag status={status} size="small" />,
        label: messages["enums.xaiCallStatus"][status],
        value: status,
      })),
      render: (_, r) =>
        r?.callStatus ? (
          <XAICallStatusTag status={r.callStatus} size="small" />
        ) : (
          "-"
        ),
    },

    {
      title: "common.clientMood",
      dataIndex: "clientMood",
      width: "150px",
      filterType: "dropdown",
      filters: Object.values(XAIClientMood).map((mood) => ({
        text: <XAIClientMoodTag mood={mood} size="small" />,
        label: messages["enums.xaiClientMood"][mood],
        value: mood,
      })),
      render: (_, r) =>
        r?.clientMood ? (
          <XAIClientMoodTag mood={r.clientMood} size="small" />
        ) : (
          "-"
        ),
    },

    {
      title: "common.disconnectionResult",
      dataIndex: "disconnectionResult",
      width: "150px",
      filterType: "dropdown",
      filters: Object.values(XAIDisconnectionResult).map((result) => ({
        text: <XAIDisconnectionResultTag result={result} size="small" />,
        label: messages["enums.xaiDisconnectionResult"][result],
        value: result,
      })),
      render: (_, r) =>
        r?.disconnectionResult ? (
          <XAIDisconnectionResultTag
            result={r.disconnectionResult}
            size="small"
          />
        ) : (
          "-"
        ),
    },

    {
      title: "common.keywords",
      dataIndex: "keywords",
      width: "150px",
      filterType: "search",
      render: (d) => <Keywords keywords={d} />,
    },

    {
      title: "common.nextStep",
      dataIndex: "nextStep",
      width: "150px",
      filterType: "dropdown",
    },

    {
      title: "common.callbackError",
      dataIndex: "callbackError",
      width: "150px",
      filterType: "search",
      render: (d) => <Tooltip title={d}>{shortenString(d, 20)}</Tooltip>,
    },

    {
      title: "common.callCost",
      dataIndex: "callCost",
      width: "150px",
      filterType: "numberRange",
      filterOptions: [
        {
          text: `0-${biggestCost}`,
          value: [0, biggestCost],
        },
      ],
      filterTitle: "common.callCost",
      render: (_, r) => (
        <CoinPriceTextFormat
          amount={Number(r?.callCost || 0)}
          showCurrencySymbol
        />
      ),
    },

    {
      title: "common.duration",
      dataIndex: "durationMs",
      width: "150px",
      render: (_, r) => <CallLength callLength={Number(r?.durationMs || 0)} />,

      sorter: true,
      filterType: "numberRange",
      filterOptions: [
        {
          text: `0-${biggestDuration}`,
          value: [0, biggestDuration],
        },
      ],
      filterTitle: "common.duration",
    },

    {
      title: "common.voiceFile",
      dataIndex: "durationMs",
      width: "200px",
      fixed: "right",
      render: (_, r) => (
        <VoicePlayer
          voiceFile={r?.voiceFile}
          showDownload={false}
          title={false}
        />
      ),
      sorter: true,
      filterType: "numberRange",
      filterOptions: [
        {
          text: `0-${biggestDuration}`,
          value: [0, biggestDuration],
        },
      ],
      filterTitle: "common.duration",
    },

    {
      title: "common.actions",
      dataIndex: "actions",
      className: "order-table-action",
      fixed: "right",
      width: "110px",
      render: (_, record) => (
        <StyledFlex className="actions">
          <ActionButton
            type="quickLook"
            onClick={() => handleSelect(record)}
            noLabel
          />
        </StyledFlex>
      ),
    },
  ];

  return generateColumns(props, columnsConfig);
};

export default getColumns;
